import { FormEvent, KeyboardEvent, useEffect, useMemo, useRef, useState } from 'react';
import { backendApi, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useModalEscape } from '../lib/useModalEscape';
import {
  EMPTY_FILTERS, PAGE_SIZE, SITES_PATH, SiteRow, SitesListResponse, buildListQuery, isEndpointMissing,
} from '../lib/sites';
import './Transfers.css';

/**
 * Site Transfers — for the customer who sees "This site is already linked to
 * another WPOS workspace". Support finds the site, picks the workspace it
 * should move to, and confirms. The move is one database transaction on the
 * backend (the site, its chats, plans, tickets and widgets), audited with the
 * admin's name and the reason.
 *
 * Uses existing backend APIs only:
 *   GET  /license/admin/sites-sandboxes          the connected sites
 *   GET  /license/admin/accounts                 workspaces, for the picker
 *   POST /license/admin/sites-sandboxes/ownership/transfer
 * Moving needs sites:write; moving credits also needs accounts:write. No key or
 * token is sent to, or shown on, this page.
 */

const TRANSFER_PATH = `${SITES_PATH}/ownership/transfer`;

interface AccountLite { id: string; email: string; name: string | null; status: string }
interface TransferResult {
  siteKey: string; siteId: string | null; fromAccountId: string | null; toAccountId: string;
  creditsMoved: number; movedRegisteredSite: boolean; warnings?: string[]; replayed?: boolean;
}

/** Idempotency key: a retry of the SAME decision must never move the site twice. */
export function newRequestId(): string {
  const rand = typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  return `admin-${rand}`.slice(0, 100);
}

const when = (iso: string | null | undefined) => (iso ? new Date(iso).toLocaleDateString() : '—');
const workspaceName = (a: { name?: string | null; email?: string | null }) => a.name || a.email || 'Unknown workspace';
const label = (a: { name?: string | null; email?: string | null }) => (a.name && a.email ? `${a.name} (${a.email})` : workspaceName(a));

export default function Transfers() {
  const { can } = useAuth();
  const canMove = can('sites', 'write');
  const canMoveCredits = can('accounts', 'write');
  const canLookupAccounts = can('accounts', 'read');

  const [site, setSite] = useState<SiteRow | null>(null);
  const [accounts, setAccounts] = useState<AccountLite[]>([]);
  const [dest, setDest] = useState<AccountLite | null>(null);
  const [manualId, setManualId] = useState('');
  const [credits, setCredits] = useState('');
  const [reason, setReason] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState<{ moved: TransferResult; from: string; to: string } | null>(null);
  const [reload, setReload] = useState(0);
  const [requestId, setRequestId] = useState(newRequestId);
  const formTop = useRef<HTMLDivElement>(null);

  const toAccountId = dest?.id ?? manualId.trim();
  // A new site or destination is a new decision: a fresh key. A retry of the
  // same decision (after a dropped connection) keeps it and replays safely.
  useEffect(() => { setRequestId(newRequestId()); }, [site?.id, toAccountId]);

  useEffect(() => {
    if (!canLookupAccounts) return;
    backendApi.get<{ accounts: AccountLite[] }>('/license/admin/accounts')
      .then((d) => setAccounts(d.accounts ?? [])).catch(() => setAccounts([]));
  }, [canLookupAccounts]);

  const creditsNum = credits.trim() === '' ? 0 : Number(credits);
  const creditsOk = Number.isInteger(creditsNum) && creditsNum >= 0 && (creditsNum === 0 || canMoveCredits);
  const sameWorkspace = !!site && toAccountId === site.account.id;
  const ready = !!site && !!toAccountId && !sameWorkspace && !!reason.trim() && creditsOk;

  function choose(s: SiteRow) {
    setSite(s); setDest(null); setManualId(''); setCredits(''); setReason(''); setError(''); setResult(null);
    formTop.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
  }

  function review(e: FormEvent) {
    e.preventDefault();
    if (ready && !busy) setConfirming(true);
  }

  async function submit() {
    if (!ready || busy || !site) return;
    setBusy(true); setError('');
    try {
      const d = await backendApi.post<{ transfer: TransferResult }>(TRANSFER_PATH, {
        siteId: site.id, toAccountId, credits: creditsNum, reason: reason.trim(), requestId,
      });
      setResult({ moved: d.transfer, from: label(site.account), to: dest ? label(dest) : toAccountId });
      setConfirming(false); setSite(null); setDest(null); setManualId(''); setCredits(''); setReason('');
      setReload((n) => n + 1);
    } catch (err: any) {
      setConfirming(false);
      setError((err as ApiError).isForbidden ? 'You do not have permission to transfer sites.' : err.message);
    } finally { setBusy(false); }
  }

  return (
    <>
      <header className="page-head">
        <div>
          <h1>Site Transfers</h1>
          <p className="muted">Move a site to another workspace when a customer sees “This site is already linked to another WPOS workspace”.</p>
        </div>
      </header>

      {error && <div className="alert alert-error">{error}</div>}
      {result && <ResultPanel result={result} onClose={() => setResult(null)} />}

      {site && canMove && (
        <div className="card" ref={formTop}>
          <h2 className="card-title">Transfer {site.url}</h2>
          <form onSubmit={review}>
            <div className="tr-from">
              <span className="muted small">Currently owned by</span>
              <strong className="tr-from-name">{workspaceName(site.account)}</strong>
              <div className="muted small">{site.account.email} · Workspace ID <span className="mono">{site.account.id}</span></div>
              <div className="muted small">{site.plan?.name ?? '—'} plan · last seen {when(site.lastSeenAt)}{site.pluginVersion ? ` · plugin v${site.pluginVersion}` : ''}</div>
            </div>

            {canLookupAccounts ? (
              <div className="field"><span>Move to workspace</span>
                <WorkspacePicker accounts={accounts} value={dest} onChange={setDest} currentId={site.account.id} />
              </div>
            ) : (
              <label className="field"><span>Destination workspace ID</span>
                <input value={manualId} onChange={(e) => setManualId(e.target.value)} /></label>
            )}
            {sameWorkspace && <div className="alert alert-error">That workspace already owns this site.</div>}

            {canMoveCredits && (
              <label className="field"><span>Credits to move (optional)</span>
                <input type="number" min={0} step={1} placeholder="0" value={credits} onChange={(e) => setCredits(e.target.value)} /></label>
            )}
            <label className="field"><span>Reason (kept in the audit log)</span>
              <textarea rows={2} placeholder="e.g. Customer moved from their old account — ticket #123"
                value={reason} onChange={(e) => setReason(e.target.value)} /></label>
            <div className="tr-actions">
              <button type="button" className="btn btn-ghost" onClick={() => setSite(null)}>Cancel</button>
              <button className="btn btn-primary" disabled={!ready || busy}>Review transfer</button>
            </div>
          </form>
        </div>
      )}

      <SitesList canMove={canMove} selectedId={site?.id ?? ''} reload={reload} onChoose={choose} />

      {confirming && site && (
        <ConfirmDialog site={site} toLabel={dest ? label(dest) : toAccountId} credits={creditsNum}
          reason={reason.trim()} busy={busy} onCancel={() => setConfirming(false)} onConfirm={submit} />
      )}
    </>
  );
}

function StateBadge({ state }: { state: SiteRow['state'] }) {
  const cls = state === 'active' ? 'badge-green' : state === 'deactivated' ? 'badge-amber' : 'badge-gray';
  return <span className={'badge ' + cls}>{state === 'archived' ? 'removed' : state}</span>;
}

/** Every connected site (real sites only; sandboxes are platform-owned), searchable and paged. */
function SitesList({ canMove, selectedId, reload, onChoose }: {
  canMove: boolean; selectedId: string; reload: number; onChoose: (s: SiteRow) => void;
}) {
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(0);
  const [data, setData] = useState<SitesListResponse | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => { // debounce the search box
    const t = setTimeout(() => { setPage(0); setQuery(search); }, 350);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    let live = true;
    setLoading(true);
    backendApi.get<SitesListResponse>(`${SITES_PATH}?${buildListQuery({ ...EMPTY_FILTERS, type: 'site', search: query, page })}`)
      .then((d) => { if (live) { setData(d); setError(''); } })
      .catch((err) => {
        if (!live) return;
        setData(null);
        setError(isEndpointMissing(err) ? 'The connected backend does not have the Sites list yet.'
          : (err as ApiError).isForbidden ? 'You do not have access to the sites list.' : `Sites could not be loaded: ${err.message}`);
      })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [query, page, reload]);

  const total = data?.total ?? 0;
  const rows = data?.sites ?? [];
  const lastPage = Math.max(0, Math.ceil(total / PAGE_SIZE) - 1);
  return (
    <div className="card" data-testid="sites-list">
      <div className="tr-search-row">
        <h2 className="card-title">Connected sites{data ? ` (${total.toLocaleString()})` : ''}</h2>
        <input className="tr-search" placeholder="Search by site, email or workspace" value={search}
          onChange={(e) => setSearch(e.target.value)} aria-label="Search connected sites" />
      </div>
      {error ? <p className="muted">{error}</p> : !data && loading ? <p className="muted">Loading…</p> : rows.length === 0 ? <p className="muted">No sites found.</p> : (
        <>
          <div className="table-wrap">
            <table className="table">
              <thead><tr><th>Site</th><th>Workspace</th><th>Plan</th><th>State</th><th>Last seen</th>{canMove && <th />}</tr></thead>
              <tbody>
                {rows.map((s) => (
                  <tr key={s.id} className={s.id === selectedId ? 'tr-selected' : undefined}>
                    <td>{s.url ?? '—'}{s.title && <span className="muted small block">{s.title}</span>}</td>
                    <td>{workspaceName(s.account)}<span className="muted small block">{s.account.email}</span></td>
                    <td className="muted">{s.plan?.name ?? '—'}</td>
                    <td><StateBadge state={s.state} /></td>
                    <td className="muted">{when(s.lastSeenAt)}</td>
                    {canMove && (
                      <td className="right">
                        <button type="button" className="btn btn-sm" disabled={!s.url || s.state === 'archived'}
                          title={s.state === 'archived' ? 'A removed site cannot be transferred' : undefined}
                          aria-label={`Transfer ${s.url ?? s.id}`} onClick={() => onChoose(s)}>Transfer</button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {total > PAGE_SIZE && (
            <div className="tr-pager">
              <button type="button" className="btn btn-sm btn-ghost" disabled={page === 0 || loading} onClick={() => setPage(page - 1)}>Previous</button>
              <span className="muted small">{page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, total)} of {total.toLocaleString()}</span>
              <button type="button" className="btn btn-sm btn-ghost" disabled={page >= lastPage || loading} onClick={() => setPage(page + 1)}>Next</button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

/** The explicit confirmation: nothing moves until this is accepted. */
function ConfirmDialog({ site, toLabel, credits, reason, busy, onCancel, onConfirm }: {
  site: SiteRow; toLabel: string; credits: number; reason: string; busy: boolean; onCancel: () => void; onConfirm: () => void;
}) {
  useModalEscape(onCancel);
  return (
    <div className="modal-scrim" onClick={onCancel}>
      <div className="modal" role="dialog" aria-modal="true" aria-label="Confirm site transfer" onClick={(e) => e.stopPropagation()}>
        <div className="modal-head"><h2>Confirm transfer</h2></div>
        <p><strong>{site.url}</strong></p>
        <div className="tr-flow">
          <div><span className="muted small">Remove from</span><strong>{label(site.account)}</strong></div>
          <span aria-hidden="true">→</span>
          <div><span className="muted small">Add to</span><strong>{toLabel}</strong></div>
        </div>
        <ul className="tr-list">
          <li>This happens in one step. If anything fails, nothing is changed.</li>
          <li>The site’s chats, plans, tickets and widgets move with it. The old workspace loses access.</li>
          {credits > 0 && <li>Up to {credits.toLocaleString()} credits move to the new workspace.</li>}
          <li>The customer then connects the site with the new workspace’s key.</li>
        </ul>
        <p className="muted small">Reason: {reason}</p>
        <div className="tr-actions">
          <button type="button" className="btn btn-ghost" onClick={onCancel} disabled={busy}>Cancel</button>
          <button type="button" className="btn btn-primary" onClick={onConfirm} disabled={busy}>{busy ? 'Transferring…' : 'Transfer site'}</button>
        </div>
      </div>
    </div>
  );
}

function ResultPanel({ result, onClose }: { result: { moved: TransferResult; from: string; to: string }; onClose: () => void }) {
  const { moved, from, to } = result;
  const steps: Array<{ key: string; state: 'ok' | 'warn' | 'info'; title: string; detail?: string }> = [
    { key: 'owner', state: 'ok', title: `Ownership moved to ${to}`, detail: moved.replayed ? 'This transfer had already been done; nothing was moved again.' : undefined },
    moved.movedRegisteredSite
      ? { key: 'removed', state: 'ok', title: `Removed from ${from}`, detail: 'The site, its chats, plans, tickets and widgets now belong to the new workspace. The old workspace’s team access was removed.' }
      : { key: 'removed', state: 'warn', title: 'Only the ownership claim moved', detail: 'No connected site was moved, so its chats and settings stay in the previous workspace.' },
  ];
  if (moved.creditsMoved > 0) steps.push({ key: 'credits', state: 'info', title: `${moved.creditsMoved.toLocaleString()} credits moved to ${to}` });
  steps.push({ key: 'customer', state: 'info', title: 'Customer: connect with the new workspace’s key',
    detail: `Ask the customer to open WPOS AI in WordPress and connect with the key of ${to}. If the site still shows the old workspace’s key, they clear the licence first. Nothing is lost: the site and its history are already in the new workspace.` });
  const attention = steps.some((s) => s.state === 'warn');
  return (
    <div className={'card tr-result' + (attention ? ' tr-attention' : '')} role="status" data-testid="transfer-result">
      <div className="modal-head">
        <h2 className="card-title">{attention ? 'Transfer complete — needs attention' : 'Transfer complete'}</h2>
        <button type="button" className="btn btn-sm btn-ghost" onClick={onClose}>Dismiss</button>
      </div>
      <ul className="tr-steps">
        {steps.map((s) => (
          <li key={s.key} className={'tr-step-' + s.state}>
            <span className="tr-mark" aria-label={s.state === 'ok' ? 'done' : s.state === 'warn' ? 'needs attention' : 'note'}>
              {s.state === 'ok' ? '✓' : s.state === 'warn' ? '!' : 'i'}</span>
            <div><strong>{s.title}</strong>{s.detail && <span className="muted small block">{s.detail}</span>}</div>
          </li>
        ))}
      </ul>
      {(moved.warnings ?? []).map((w) => <p key={w} className="muted small">{w}</p>)}
    </div>
  );
}

function Highlight({ text, q }: { text: string; q: string }) {
  const i = q ? text.toLowerCase().indexOf(q.toLowerCase()) : -1;
  if (i < 0) return <>{text}</>;
  return <>{text.slice(0, i)}<mark className="combo-mark">{text.slice(i, i + q.length)}</mark>{text.slice(i + q.length)}</>;
}

/** Searchable single-select: a popover list under the input, keyboard driven. */
function WorkspacePicker({ accounts, value, onChange, currentId }: {
  accounts: AccountLite[]; value: AccountLite | null; onChange: (a: AccountLite | null) => void; currentId?: string;
}) {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const root = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const matches = useMemo(() => {
    const t = q.trim().toLowerCase();
    const list = t
      ? accounts.filter((a) => a.email.toLowerCase().includes(t) || (a.name ?? '').toLowerCase().includes(t) || a.id === t)
      : accounts;
    return list.slice(0, 50);
  }, [accounts, q]);

  // Why a workspace cannot be chosen: it already owns the site, or it is not active.
  const blocked = (a: AccountLite) => (a.id === currentId ? 'current owner' : a.status !== 'active' ? a.status.replace(/_/g, ' ') : '');

  useEffect(() => { setActive(0); }, [q]);
  useEffect(() => {
    if (!open) return;
    const off = (e: MouseEvent) => { if (!root.current?.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', off);
    return () => document.removeEventListener('mousedown', off);
  }, [open]);
  useEffect(() => {
    (listRef.current?.children[active] as HTMLElement | undefined)?.scrollIntoView?.({ block: 'nearest' });
  }, [active, open]);

  function pick(a: AccountLite) { if (blocked(a)) return; onChange(a); setOpen(false); setQ(''); }
  function onKey(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); setActive((i) => Math.min(i + 1, matches.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((i) => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter' && open && matches[active]) { e.preventDefault(); pick(matches[active]); }
    else if (e.key === 'Escape') setOpen(false);
  }

  if (value) {
    return (
      <div className="combo-selected">
        <div><strong>{value.email}</strong>{value.name && <span className="muted small block">{value.name}</span>}</div>
        <button type="button" className="btn btn-sm btn-ghost" onClick={() => onChange(null)}>Change</button>
      </div>
    );
  }
  return (
    <div className="combo" ref={root}>
      <input role="combobox" aria-expanded={open} aria-controls="ws-listbox" aria-autocomplete="list"
        placeholder="Search by email or name" value={q} autoComplete="off"
        onFocus={() => setOpen(true)} onChange={(e) => { setQ(e.target.value); setOpen(true); }} onKeyDown={onKey} />
      {open && (
        <ul className="combo-list" id="ws-listbox" role="listbox" ref={listRef}>
          {matches.length === 0 && <li className="combo-empty">No matching workspaces</li>}
          {matches.map((a, i) => {
            const why = blocked(a);
            return (
              <li key={a.id} role="option" aria-selected={i === active} aria-disabled={!!why}
                className={'combo-opt' + (i === active ? ' active' : '') + (why ? ' disabled' : '')}
                onMouseEnter={() => setActive(i)} onMouseDown={(e) => { e.preventDefault(); pick(a); }}>
                <span className="combo-main"><Highlight text={a.email} q={q.trim()} /></span>
                <span className="combo-sub muted small">{a.name ? <Highlight text={a.name} q={q.trim()} /> : null}</span>
                {why && <span className="badge badge-amber">{why}</span>}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
